import {
  Popover,
  Box,
  Stack,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { Roles } from 'src/Constants/BorgEnums';
import { UserFilters } from 'src/Pages/Admin/Users/UsersAdminPage';
import { Organization } from 'src/Constants/Types';

type Props = {
  anchorEl: HTMLElement | null;
  open: boolean;
  filters: UserFilters;
  onChange: (filters: UserFilters) => void;
  onClose: () => void;
  users: any[]; // Array of users to extract filter options from
  organizations: Organization[];
};

export const UserFiltersPopover = ({
  anchorEl,
  open,
  filters,
  onChange,
  onClose,
  users,
  organizations,
}: Props) => {
  // Get unique roles that exist in the user data
  const availableRoles = Array.from(new Set(users.map(user => user.roles?.[0]).filter(Boolean)));
  
  // Get unique organizations that exist in the user data
  const availableOrganizations = organizations.filter(org => 
    users.some(user => user.organizationId === org.id)
  );

  // Status options
  const statusOptions = [
    { value: true, label: 'Enabled' },
    { value: false, label: 'Disabled' }
  ];

  const getUsersExcludingFilter = (excludeFilterType: 'organization' | 'role' | 'status') => {
    return users.filter(user => {
      // Apply all filters except the specified one
      if (excludeFilterType !== 'organization' && filters.organizationIds && filters.organizationIds.length > 0) {
        if (!filters.organizationIds.includes(user.organizationId)) return false;
      }
      if (excludeFilterType !== 'role' && filters.roles && filters.roles.length > 0) {
        if (!filters.roles.includes(user.roles[0])) return false;
      }
      if (excludeFilterType !== 'status' && filters.statuses && filters.statuses.length > 0) {
        if (!filters.statuses.includes(user.enabled)) return false;
      }
      return true;
    });
  };

  const getOrganizationCount = (orgId: string): number => {
    const availableUsers = getUsersExcludingFilter('organization');
    return availableUsers.filter(user => user.organizationId === orgId).length;
  };

  const getRoleCount = (role: string): number => {
    const availableUsers = getUsersExcludingFilter('role');
    return availableUsers.filter(user => user.roles?.[0] === role).length;
  };

  const getStatusCount = (enabled: boolean): number => {
    const availableUsers = getUsersExcludingFilter('status');
    return availableUsers.filter(user => user.enabled === enabled).length;
  };

  const handleOrganizationChange = (orgId: string, checked: boolean) => {
    const current = filters.organizationIds || [];
    const updated = checked 
      ? [...current, orgId]
      : current.filter(id => id !== orgId);
    
    onChange({
      ...filters,
      organizationIds: updated.length > 0 ? updated : undefined
    });
  };

  const handleRoleChange = (role: Roles, checked: boolean) => {
    const current = filters.roles || [];
    const updated = checked
      ? [...current, role]
      : current.filter(r => r !== role);
    
    onChange({
      ...filters,
      roles: updated.length > 0 ? updated : undefined
    });
  };

  const handleStatusChange = (status: boolean, checked: boolean) => {
    const current = filters.statuses || [];
    const updated = checked
      ? [...current, status]
      : current.filter(s => s !== status);
    
    onChange({
      ...filters,
      statuses: updated.length > 0 ? updated : undefined
    });
  };

  const clearFilters = () => onChange({});

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ p: 2, width: 320, maxHeight: 400, overflow: 'auto' }}>
        <Stack spacing={2}>
          {/* Organizations */}
          {availableOrganizations.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight="bold">
                Organizations
              </Typography>
              <Stack spacing={1} sx={{ maxHeight: 120, overflow: 'auto' }}>
                {availableOrganizations.map((org) => {
                  const count = getOrganizationCount(org.id);
                  return (
                    <FormControlLabel
                      key={org.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.organizationIds?.includes(org.id) || false}
                          onChange={(e) => handleOrganizationChange(org.id, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{org.name}</span>
                          <Typography variant="caption" color="text.secondary">
                            ({count})
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              </Stack>
              <Divider />
            </>
          )}

          {/* Roles */}
          {availableRoles.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight="bold">
                Roles
              </Typography>
              <Stack spacing={1}>
                {availableRoles.map((role) => {
                  const count = getRoleCount(role);
                  return (
                    <FormControlLabel
                      key={role}
                      control={
                        <Checkbox
                          size="small"
                          checked={filters.roles?.includes(role) || false}
                          onChange={(e) => handleRoleChange(role as Roles, e.target.checked)}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{role}</span>
                          <Typography variant="caption" color="text.secondary">
                            ({count})
                          </Typography>
                        </Box>
                      }
                    />
                  );
                })}
              </Stack>
              <Divider />
            </>
          )}

          {/* Status */}
          <Typography variant="subtitle2" fontWeight="bold">
            Status
          </Typography>
          <Stack spacing={1}>
            {statusOptions.map((option) => {
              const count = getStatusCount(option.value);
              return (
                <FormControlLabel
                  key={option.value.toString()}
                  control={
                    <Checkbox
                      size="small"
                      checked={filters.statuses?.includes(option.value) || false}
                      onChange={(e) => handleStatusChange(option.value, e.target.checked)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <span>{option.label}</span>
                      <Typography variant="caption" color="text.secondary">
                        ({count})
                      </Typography>
                    </Box>
                  }
                />
              );
            })}
          </Stack>

          {/* Actions */}
          <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
            <Button onClick={clearFilters}>Clear All</Button>
            <Button variant="contained" onClick={onClose}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
};
